import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import PhoneNumberInput, {
  defaultCountryOptions,
} from "../../Components/Input/PhoneNumber";
import { Form, Button, Spinner } from "react-bootstrap";
import auth from "../../Account/Auth";
import be from "../../BE";

const Heading = () => (
  <div>
    <div className="tw-text-lg tw-font-bold">Getting Started</div>
    <div className="tw-text-sm tw-text-gray-600">
      Set up your workspace by completing the checklist. Most users finish in
      under 2 minutes!
    </div>
  </div>
);

const Progress = ({ completed, total }) => (
  <div className="my-1">
    <div className="tw-flex tw-justify-between tw-mb-1">
      <span className="tw-text-base tw-font-medium tw-text-green-700 dark:tw-text-white">
        Your progress
      </span>
      <span className="tw-text-sm tw-font-medium tw-text-green-700 dark:tw-text-white">
        {parseInt((completed * 100) / total)}%
      </span>
    </div>
    <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2.5 dark:tw-bg-gray-700">
      <div
        className="tw-bg-green-600 tw-h-2.5 tw-rounded-full"
        style={{ width: `${parseInt((completed * 100) / total)}%` }}
      />
    </div>
  </div>
);

const CTA = ({ text, onClick }) => (
  <button
    className="tw-bg-black tw-px-4 tw-py-1 tw-rounded-md tw-text-white hover:tw-bg-gray-800 tw-min-w-28 tw-max-h-10"
    onClick={onClick}
  >
    {text}
  </button>
);

const WhatsappInline = React.memo(function WhatsappInline({
  selectedCountry,
  setSelectedCountry,
  inputValue,
  setInputValue,
  processing,
  handleSubmit,
}) {
  return (
    <Form
      onSubmit={handleSubmit}
      className="tw-my-2 tw-flex tw-flex-col tw-items-center tw-max-w-sm tw-mx-auto"
    >
      <small>Save your WhatsApp number to get 20 free notifications!</small>
      <div className="tw-flex tw-w-full">
        <PhoneNumberInput
          selectedCountry={selectedCountry}
          onSelectCountry={setSelectedCountry}
          number={inputValue}
          onChangeNumber={setInputValue}
          disabled={processing}
          required
        />
        <Button
          variant="primary"
          type="submit"
          disabled={processing}
          style={{
            height: 42,
            background: "#374151",
            border: "none",
            minWidth: 80,
          }}
          className="tw-rounded tw-ml-2"
        >
          {processing ? (
            <Spinner size="sm" animation="border" variant="light" />
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </Form>
  );
});

const Task = ({
  id,
  icon,
  title,
  description,
  cta,
  done,
  showWhatsappInline,
  selectedCountry,
  setSelectedCountry,
  inputValue,
  setInputValue,
  processing,
  skipping,
  handleSubmit,
  onSkip,
}) => (
  <div
    className={`tw-my-4 tw-p-2 tw-flex tw-flex-col tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg ${
      done
        ? "tw-bg-gray-200 dark:!tw-bg-gray-700"
        : "tw-bg-white dark:tw-bg-gray-900"
    }`}
  >
    <div className="tw-flex tw-flex-row tw-w-full">
      <div className="tw-flex tw-w-full tw-p-4">
        <div className="tw-flex tw-my-auto tw-ml-2 tw-mr-4">{icon}</div>
        <div className="tw-flex tw-flex-col">
          <div className={`tw-font-bold ${done && "tw-text-gray-500"}`}>
            {title}
          </div>
          <div className={`${done && "tw-text-gray-500"}`}>{description}</div>
        </div>
        {/* only show CTA if task is neither done nor being skipped */}
        {!done && !skipping && (
          <div className="tw-flex tw-ml-auto tw-my-auto">{cta}</div>
        )}
        {/* show "Done" indicator if task is done or skipping */}
        {(done || skipping) && (
          <div className="tw-flex tw-my-auto tw-ml-auto tw-items-center tw-text-green-600">
            <i className="tw-mr-1 fa-solid fa-check"></i>
            <span>Done</span>
          </div>
        )}
      </div>
      {/* only show skip button if task is not done and not already skipping */}
      {!done && !skipping && (
        <button
          onClick={() => onSkip(id)}
          className="tw-p-0 tw-mb-auto tw-ml-2 tw-text-gray-500 hover:tw-text-gray-700"
        >
          <i className="fa-solid fa-times"></i>
        </button>
      )}
    </div>
    {id === "whatsapp" && showWhatsappInline && (
      <WhatsappInline
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        inputValue={inputValue}
        setInputValue={setInputValue}
        processing={processing}
        handleSubmit={handleSubmit}
      />
    )}
  </div>
);

function PostOnboarding() {
  const [response, setResponse] = useState({});
  const [tasksState, setTasksState] = useState([]);
  const [showWhatsappInline, setShowWhatsappInline] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    defaultCountryOptions[1]
  );
  const [inputValue, setInputValue] = useState("");
  const [processing, setProcessing] = useState(false);
  const [skippingTaskIds, setSkippingTaskIds] = useState([]);
  const history = useHistory();

  // return the promise so callers can await completion
  const fetchTasks = useCallback(() => {
    return auth
      .isAuthenticated()
      .then(() => be.get("Onboarding", "/onboarding/tasks", false, true, true))
      .then((res) => {
        setResponse(res);
        setTasksState(res.tasks || []);
      })
      .catch((error) =>
        console.error("[post-onboarding] error fetching tasks:", error)
      );
  }, [history]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!inputValue) return;
      setProcessing(true);
      auth
        .isAuthenticated()
        .then(() =>
          be.post(
            "account-jubilant",
            "/settings/whatsapp",
            { country: selectedCountry?.dial, number: inputValue },
            true,
            true
          )
        )
        .then(() => {
          setProcessing(false);
          setShowWhatsappInline(false);
          return fetchTasks();
        })
        .catch((err) => {
          console.error(err);
          setProcessing(false);
        });
    },
    [inputValue, selectedCountry, fetchTasks]
  );

  const sendIntent = useCallback(
    (_intent) => {
      auth
        .isAuthenticated()
        .then(() => {
          be.post(
            "Onboarding",
            "/onboarding/tasks/intent",
            { stage: _intent },
            true,
            true
          )
            .then(() => console.log("[post-onboarding] sent intent:", _intent))
            .then(fetchTasks)
            .catch((error) =>
              console.error("[post-onboarding] error sending intent:", error)
            );
        })
        .catch(() => history.push("/login"));
    },
    [history, fetchTasks]
  );

  const handleSkip = useCallback(
    (taskId) => {
      // mark this task as skipping
      setSkippingTaskIds((prev) => [...prev, taskId]);
      auth
        .isAuthenticated()
        .then(() =>
          be.post(
            "Onboarding",
            `/onboarding/tasks/skip/${taskId}`,
            {},
            true,
            true
          )
        )
        .then(() => fetchTasks())
        .then(() =>
          // clear skipping state for this task
          setSkippingTaskIds((prev) => prev.filter((id) => id !== taskId))
        )
        .catch((error) => {
          console.error("[post-onboarding] error skipping task:", error);
          setSkippingTaskIds((prev) => prev.filter((id) => id !== taskId));
        });
    },
    [fetchTasks, history]
  );

  const tasksMeta = {
    calendar: {
      icon: (
        <img width="28px" src="/app-assets/png/google-logo.png" alt="Google" />
      ),
      title: "Connect Google Calendar",
      description:
        "Automatically sync and manage your meetings with built-in scheduling",
      cta: (
        <CTA
          text="Connect"
          onClick={() => {
            sendIntent("calendar");
            history.push("/calendarconnections");
          }}
        />
      ),
    },
    whatsapp: {
      icon: (
        <i className="fa-brands fa-whatsapp tw-text-2xl tw-text-[#2dec62]"></i>
      ),
      title: "Enable Whatsapp Notifications",
      description:
        "Receive real-time alerts directly on Whatsapp when someone books a meeting",
      cta: (
        <CTA
          text="Enable"
          onClick={() => {
            sendIntent("whatsapp");
            setShowWhatsappInline(true);
          }}
        />
      ),
    },
    stripe: {
      icon: (
        <i className="fa-brands fa-stripe-s tw-text-2xl tw-text-[#5b4eed]"></i>
      ),
      title: "Connect Stripe Payments",
      description:
        "Start earning with 0% transaction fees on all your services",
      cta: (
        <CTA
          text="Set up"
          onClick={() => {
            sendIntent("stripe");
            history.push("/integrations#stripe");
          }}
        />
      ),
    },
  };

  return (
    <div>
      {response && Object.keys(response).length > 0 && <Heading />}
      {response?.completedTasks >= 0 && response?.maxTasks >= 1 && (
        <Progress
          completed={response.completedTasks}
          total={response.maxTasks}
        />
      )}
      {tasksState
        .filter((task) => !task.done && !task.skip)
        .map((task) => (
          <Task
            key={`post-onboarding-step-${task.id}`}
            id={task.id}
            done={task.done}
            icon={tasksMeta[task.id]?.icon}
            title={tasksMeta[task.id]?.title}
            description={tasksMeta[task.id]?.description}
            cta={tasksMeta[task.id]?.cta}
            showWhatsappInline={showWhatsappInline}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            inputValue={inputValue}
            setInputValue={setInputValue}
            processing={processing}
            skipping={skippingTaskIds.includes(task.id)}
            handleSubmit={handleSubmit}
            onSkip={handleSkip}
          />
        ))}
    </div>
  );
}

export default PostOnboarding;
